import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AllViewCountActionFetchData } from "../../../redux/actions/AllViewCountAction";
import ReactGA from "react-ga4";
import "./NewsMakerDetail.css";
import "../election/PoliticsNewsSection.css";
import axios from "axios";
import { baseUrl, generateResizedImageUrl } from "../../../utils/httpService";

const colorsPalette = [
  {
    color: "linear-gradient(135deg, #FF9933 0%, #FFFFFF 50%, #138808 100%)",
    glowColor: "rgba(255, 153, 51, 0.15)",
    color1: "#FF9933"
  },
  {
    color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    glowColor: "rgba(118, 75, 162, 0.15)",
    color1: "#667eea"
  },
  {
    color: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
    glowColor: "rgba(255, 154, 158, 0.15)",
    color1: "#ff9a9e"
  },
  {
    color: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)",
    glowColor: "rgba(253, 160, 133, 0.15)",
    color1: "#fda085"
  },
  {
    color: "linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)",
    glowColor: "rgba(180, 144, 202, 0.15)",
    color1: "#b490ca"
  },
  {
    color: "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)",
    glowColor: "rgba(161, 196, 253, 0.15)",
    color1: "#a1c4fd"
  },
  {
    color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    glowColor: "rgba(245, 87, 108, 0.15)",
    color1: "#f5576c"
  },
  {
    color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    glowColor: "rgba(0, 242, 254, 0.15)",
    color1: "#4facfe"
  }
];

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0] ? parts[0].substring(0, 2).toUpperCase() : "";
};

const NewsMakerDetail = () => {
  const { value } = useParams();
  const dispatch = useDispatch();

  const [pol, setPol] = useState("");

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Asset base URL from Redux or default CDN fallback
  const { 21: assetbaseUrl } = useSelector(
    (state) => state?.SettingReducer?.data || {}
  );

  // Fetch details & related posts
  useEffect(() => {
    const fetchNewsMakerDetail = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseUrl}news-makers/${value}?page=1&limit=20`);
        if (response.data && response.data.status && response.data.data) {
          const detail = response.data.data;
          const palette = colorsPalette[detail.id % colorsPalette.length] || colorsPalette[0];
          setPol({
            name: detail.title || "",
            gujaratiName: detail.title || "",
            designation: detail.sub_title || "",
            bannerTitle: detail.sub_title || "",
            avatar: generateResizedImageUrl(200, 200, detail.image),
            banner: generateResizedImageUrl(1200, 400, detail.banner_image),
            color: palette.color,
            glowColor: palette.glowColor,
            color1: palette.color1,
            initials: getInitials(detail.title)
          });
          const related = response.data.relatedPosts?.result || [];
          setArticles(related);
          setPage(1);
          if (related.length < 20) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        }
      } catch (err) {
        console.error("Failed to load newsmaker details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (value) {
      fetchNewsMakerDetail();
    }
  }, [value, assetbaseUrl]);

  // Load more paginated posts
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const response = await axios.get(`${baseUrl}news-makers/${value}?page=${nextPage}&limit=20`);
      if (response.data && response.data.status) {
        const related = response.data.relatedPosts?.result || [];
        if (related.length === 0) {
          setHasMore(false);
        } else {
          setArticles((prev) => [...prev, ...related]);
          setPage(nextPage);
          if (related.length < 20) {
            setHasMore(false);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load more newsmaker articles:", err);
    } finally {
      setLoadingMore(false);
    }
  };


  // Register page view
  useEffect(() => {
    dispatch(AllViewCountActionFetchData(window.location.pathname));
    if (ReactGA && pol.name) {
      ReactGA.event({
        category: "NewsMaker Detail Page",
        action: "View Profile",
        label: pol.name
      });
    }
  }, [value, dispatch, pol.name]);

  // Filter out top articles with images for carousel slideshow
  const carouselArticles = articles.filter(a => a.media).slice(0, 5);

  // Carousel slider navigations
  const handlePrevSlide = () => {
    if (carouselArticles.length === 0) return;
    setActiveSlide((prev) => (prev === 0 ? carouselArticles.length - 1 : prev - 1));
  };

  const handleNextSlide = () => {
    if (carouselArticles.length === 0) return;
    setActiveSlide((prev) => (prev === carouselArticles.length - 1 ? 0 : prev + 1));
  };

  // Auto-slide every 5 seconds
  useEffect(() => {
    if (carouselArticles.length <= 1) return;

    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev === carouselArticles.length - 1 ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [carouselArticles.length]);

  // Filter out articles that are in the carousel to avoid duplication
  const latestNewsArticles = articles.filter(
    (article) => !carouselArticles.some((c) => c.id === article.id)
  );

  // Fallback: If all articles have media and are in the carousel,
  // we exclude at least the first/featured one to avoid the immediate visual repetition.
  const displayLatestNews = latestNewsArticles.length > 0
    ? latestNewsArticles
    : articles.slice(1);

  // Slice first 2 articles to display under the 350x250 ad on the right of the spotlight slider
  const adUnderArticles = displayLatestNews.slice(0, 2);
  const remainingLatest = displayLatestNews.slice(2);

  return (
    <>
      <main className="newsmaker-detail-page-wrapper">
        {/* Banner Section - Image 1 */}
        <div className="container mt-4">
          <section className="newsmaker-detail-banner-wrap" style={{ "--nm-glow-soft": pol.glowColor }}>
            <div className="newsmaker-detail-banner-img" style={{ backgroundImage: `url(${pol.banner})` }}>
              <div className="newsmaker-banner-mesh-backdrop"></div>
              <div className="newsmaker-banner-content-inner">
                <h2 className="newsmaker-banner-badge">{pol.bannerTitle}</h2>
                <h1 className="newsmaker-banner-fullname">{pol.name}</h1>
              </div>
            </div>

            {/* Overlapping Avatar Profile */}
            <div className="newsmaker-avatar-overlapping-frame">
              <div className="avatar-gold-glow-ring" style={{ background: pol.color }}></div>
              <div className="avatar-img-circle">
                {pol.avatar ? (
                  <img
                    src={pol.avatar}
                    alt={pol.name}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div className="avatar-monogram-fallback" style={{ background: pol.color, display: pol.avatar ? "none" : "flex" }}>
                  {pol.initials}
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="container newsmaker-detail-container">

          {/* Carousel Showcase Section - Image 2 */}
          {loading ? (
            <div className="newsmaker-skeleton-slider py-5">
              <div className="skeleton-line-header"></div>
              <div className="skeleton-card-body"></div>
            </div>
          ) : carouselArticles.length > 0 ? (
            <div className="row mt-5 align-items-stretch">
              {/* Left Column: Spotlight Moments Slider */}
              <div className="col-xl-9 col-lg-9 col-md-12 mb-4 mb-lg-0">
                <section className="newsmaker-slider-section h-100">
                  <div className="newsmaker-section-header d-flex justify-content-between align-items-center mb-3">
                    <div className="section-title-wrapper">
                      <h3 className="section-main-title"> Spotlight Moments</h3>
                    </div>
                    {/* Arrow Controllers */}
                    <div className="newsmakers-arrow-nav">
                      <button className="newsmakers-arrow-btn prev-btn" onClick={handlePrevSlide} aria-label="Previous Slide">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                      </button>
                      <button className="newsmakers-arrow-btn next-btn" onClick={handleNextSlide} aria-label="Next Slide">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Slider Viewport */}
                  <div className="newsmaker-slider-viewport">
                    {carouselArticles.map((article, idx) => {
                      const isActive = idx === activeSlide;
                      return (
                        <div
                          key={`slide-${article.id || idx}`}
                          className={`newsmaker-slide-card ${isActive ? "active" : ""}`}
                        >
                          <Link target="_parent" to={`/${article.url}`}>
                            <div className="slide-image-wrapper">
                              <img
                                src={generateResizedImageUrl(869, 380, article.media)}
                                alt={article.media_title || article.title}
                                loading="lazy"
                              />
                              <div className="slide-overlay-gradient"></div>
                              {article.video && (
                                <div className="slide-play-indicator">
                                  <i className="fas fa-play"></i>
                                </div>
                              )}
                              <div className="slide-caption-details">
                                <h3 className="slide-tagline">{article.tagline || pol.gujaratiName}</h3>
                                <h2 className="slide-title-text">{article.title}</h2>
                                <span className="slide-timestamp">
                                  <i className="far fa-clock"></i> {article.publish_date}
                                </span>
                              </div>
                            </div>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>

              {/* Right Column: 350x250 Ad Container + Related News */}
              <div className="col-xl-3 col-lg-3 col-md-12 text-center d-flex flex-column">

                <div className="ads_new_styling M-R_banner_Subhome" style={{ height: "250px", width: "300px" }}>
                  <div id="ub-banner-c-2"></div>
                </div>
                {/* News under Ad */}
                {adUnderArticles.length > 0 && (
                  <div className="ad-under-news-vault text-start" style={{ marginTop: '10px' }}>
                    {adUnderArticles.map((item, idx) => (
                      <Link key={`ad-under-${item.id || idx}`} to={`/${item.url}`} className="side-item-link" style={{ marginBottom: '15px' }}>
                        <div className="side-item-thumb">
                          <img src={generateResizedImageUrl(160, 100, item.media)} alt={item.title} />
                          {item.post_type === 4 && <i className="fas fa-play mini-play"></i>}
                        </div>
                        <div className="side-item-info">
                          <span className="side-tag">{item.tagline || pol.gujaratiName}</span>
                          <h3 className="side-title" style={{ fontSize: '13px', lineHeight: '1.4' }}>{item.title}</h3>
                          {/* <span className="side-time">{item.publish_date}</span> */}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Grid Layout Latest News Section - Replicating PoliticsNewsSection.js layout */}
          <section className="politics-news-section-premium" style={{ marginTop: "50px", marginBottom: "80px" }}>
            <div className="section-header-premium">
              <h3 className="title-guj">નવીનતમ સમાચાર</h3>
              <h3 className="title-eng">LATEST NEWS</h3>
              <div className="accent-line"></div>
            </div>

            {loading ? (
              <div className="newsmaker-skeleton-grid py-4">
                <div className="row">
                  <div className="col-md-7"><div className="skeleton-grid-card" style={{ height: '440px' }}></div></div>
                  <div className="col-md-5">
                    <div className="skeleton-grid-card mb-3" style={{ height: '120px' }}></div>
                    <div className="skeleton-grid-card mb-3" style={{ height: '120px' }}></div>
                    <div className="skeleton-grid-card" style={{ height: '120px' }}></div>
                  </div>
                </div>
              </div>
            ) : articles.length === 0 ? (
              <div className="newsmaker-empty-news-state text-center py-5">
                <div className="empty-state-graphic mb-3">📰</div>
                <h3>કોઈ સમાચાર મળ્યા નથી</h3>
                <p className="text-muted">હાલમાં {pol.name} ના કોઈ સક્રિય સમાચાર ઉપલબ્ધ નથી.</p>
              </div>
            ) : remainingLatest.length > 0 ? (
              (() => {
                const featured = remainingLatest[0];
                const sideList = remainingLatest.slice(1, 4);
                const bottomGrid = remainingLatest.slice(4);

                return (
                  <div className="news-main-container">
                    <div className="row">
                      {/* Featured Article */}
                      {featured && (
                        <div className="col-xl-8 col-lg-12 featured-vault">
                          <Link to={`/${featured.url}`} className="featured-story-link">
                            <div className="featured-image-wrap">
                              <img
                                src={generateResizedImageUrl(812, 482, featured.media)}
                                alt={featured.media_title || featured.title}
                              />
                              {featured.post_type === 4 && (
                                <div className="play-overlay"><i className="fas fa-play"></i></div>
                              )}
                              <div className="featured-caption">
                                <span className="tag-badge">{featured.tagline || pol.gujaratiName || 'SPOTLIGHT'}</span>
                                <h1 className="featured-title">{featured.title}</h1>
                                {/* <p className="featured-meta">
                                  <i className="far fa-clock"></i> {featured.publish_date}
                                </p> */}
                              </div>
                            </div>
                          </Link>
                        </div>
                      )}

                      {/* Side List */}
                      <div className="col-xl-4 col-lg-12 side-rail-vault">
                        {sideList.map((item, idx) => (
                          <Link key={`side-${item.id || idx}`} to={`/${item.url}`} className="side-item-link">
                            <div className="side-item-thumb">
                              <img src={generateResizedImageUrl(160, 100, item.media)} alt={item.title} />
                              {item.post_type === 4 && <i className="fas fa-play mini-play"></i>}
                            </div>
                            <div className="side-item-info">
                              <span className="side-tag">{item.tagline || pol.gujaratiName}</span>
                              <h3 className="side-title">{item.title}</h3>
                              <span className="side-card-readmore">
                                વધુ વાંચો <i className="fa fa-angle-right"></i>
                              </span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Bottom Grid */}
                    {bottomGrid.length > 0 && (
                      <div className="row bottom-grid-vault">
                        {bottomGrid.map((item, idx) => (
                          <div key={`bottom-${item.id || idx}`} className="col-md-4 col-sm-6">
                            <Link to={`/${item.url}`} className="grid-item-link">
                              <div className="grid-thumb">
                                <img src={generateResizedImageUrl(392, 220, item.media)} alt={item.title} />
                                {item.post_type === 4 && <i className="fas fa-play mini-play"></i>}
                              </div>
                              <div className="grid-info">
                                <h2 className="grid-title">{item.title}</h2>
                                <span className="side-card-readmore">
                                  વધુ વાંચો <i className="fa fa-angle-right"></i>
                                </span>
                              </div>
                            </Link>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Dynamic Load More Button */}
                    {hasMore && (
                      <div className="load-more-wrap">
                        <button
                          className="load-more-btn"
                          onClick={handleLoadMore}
                          disabled={loadingMore}
                        >
                          {loadingMore ? "લોડ થઈ રહ્યું છે..." : "વધુ સમાચાર જુઓ"}{" "}
                          <i className="fas fa-chevron-down"></i>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()
            ) : null}
          </section>

        </div>

      </main>
    </>
  );
};

export default NewsMakerDetail;